import { EventEmitter } from 'eventemitter3';
import {
  ConnectionError,
  DisconnectionError,
  SendError,
  ValidationError,
} from '../errors/index.js';
import type { Transport, TransportConfig } from './transport.js';

/**
 * Connection state for transport implementations
 */
type ConnectionState = 'disconnected' | 'connecting' | 'connected';

/**
 * Abstract base class for transport implementations.
 *
 * Provides common functionality like connection state management, error handling,
 * and configuration validation while allowing transport-specific implementations
 * through the template method pattern.
 *
 * @template TConfig - Transport-specific configuration type extending TransportConfig
 */
export abstract class BaseTransport<
    TConfig extends TransportConfig = TransportConfig,
  >
  extends EventEmitter
  implements Transport
{
  protected readonly config: TConfig;
  protected connectionState: ConnectionState = 'disconnected';
  private connectStartTime: Date | undefined;

  constructor(config: TConfig) {
    super();

    // Validate configuration
    this.validateConfig(config);
    this.config = { ...config };
  }

  // Abstract methods for transport-specific implementation

  /**
   * Transport-specific connection implementation.
   * Should establish the physical/logical connection.
   * @throws {Error} Transport-specific connection errors
   */
  protected abstract doConnect(): Promise<void>;

  /**
   * Transport-specific disconnection implementation.
   * Should clean up and close the connection.
   * @throws {Error} Transport-specific disconnection errors
   */
  protected abstract doDisconnect(): Promise<void>;

  /**
   * Transport-specific send implementation.
   * Should transmit the data through the transport medium.
   * @param data - Binary data to send
   * @throws {Error} Transport-specific send errors
   */
  protected abstract doSend(data: Uint8Array): Promise<void>;

  // Public interface implementation (Transport interface)

  /**
   * Connect to the transport layer.
   * Handles state management and error wrapping around doConnect().
   */
  async connect(): Promise<void> {
    // Validate state
    if (this.connectionState === 'connected') {
      return; // Already connected
    }

    if (this.connectionState === 'connecting') {
      throw new ConnectionError('Connection already in progress', {
        context: this.getErrorContext('connect'),
      });
    }

    this.connectionState = 'connecting';
    this.connectStartTime = new Date();

    try {
      await this.doConnect();
      this.connectionState = 'connected';
      this.emit('connect');
    } catch (error) {
      this.connectionState = 'disconnected';
      this.connectStartTime = undefined;

      throw new ConnectionError(
        `Failed to connect via ${this.constructor.name}`,
        {
          cause: error instanceof Error ? error : new Error(String(error)),
          context: this.getErrorContext('connect'),
        }
      );
    }
  }

  /**
   * Disconnect from the transport layer.
   * Handles state management and error wrapping around doDisconnect().
   */
  async disconnect(): Promise<void> {
    if (this.connectionState === 'disconnected') {
      return; // Already disconnected
    }

    const wasConnected = this.connectionState === 'connected';

    try {
      await this.doDisconnect();
    } catch (error) {
      throw new DisconnectionError(
        `Failed to disconnect from ${this.constructor.name}`,
        {
          cause: error instanceof Error ? error : new Error(String(error)),
          context: this.getErrorContext('disconnect'),
        }
      );
    } finally {
      this.connectionState = 'disconnected';
      this.connectStartTime = undefined;

      if (wasConnected) {
        this.emit('disconnect');
      }
    }
  }

  /**
   * Check if transport is currently connected.
   */
  isConnected(): boolean {
    return this.connectionState === 'connected';
  }

  /**
   * Send raw binary data through the transport.
   * Validates state and data before calling doSend().
   */
  async send(data: Uint8Array): Promise<void> {
    // Validate connection state
    if (this.connectionState !== 'connected') {
      throw new SendError('Cannot send data: transport not connected', {
        context: this.getErrorContext('send', { dataSize: data.length }),
      });
    }

    // Validate data
    if (!(data instanceof Uint8Array)) {
      throw new ValidationError('Data must be a Uint8Array', {
        context: this.getErrorContext('send', { dataType: typeof data }),
      });
    }

    if (data.length === 0) {
      throw new ValidationError('Cannot send empty data', {
        context: this.getErrorContext('send', { dataSize: 0 }),
      });
    }

    try {
      await this.doSend(data);
    } catch (error) {
      throw new SendError(
        `Failed to send ${data.length} bytes via ${this.constructor.name}`,
        {
          cause: error instanceof Error ? error : new Error(String(error)),
          context: this.getErrorContext('send', { dataSize: data.length }),
        }
      );
    }
  }

  /**
   * Get the local device address.
   */
  getLocalAddress(): number {
    return this.config.localAddress;
  }

  /**
   * Get the local group address.
   */
  getGroupAddress(): number {
    return this.config.groupAddress;
  }

  // Protected helper methods

  /**
   * Validate transport configuration.
   * @param config - Configuration to validate
   * @throws {ValidationError} If configuration is invalid
   */
  protected validateConfig(config: TConfig): void {
    if (!config) {
      throw new ValidationError('Transport configuration is required');
    }

    // Validate localAddress
    if (typeof config.localAddress !== 'number') {
      throw new ValidationError('localAddress must be a number', {
        context: { localAddress: config.localAddress },
      });
    }

    if (
      !Number.isInteger(config.localAddress) ||
      config.localAddress < 0 ||
      config.localAddress > 255
    ) {
      throw new ValidationError(
        'localAddress must be an integer between 0-255',
        {
          context: { localAddress: config.localAddress },
        }
      );
    }

    // Validate groupAddress
    if (typeof config.groupAddress !== 'number') {
      throw new ValidationError('groupAddress must be a number', {
        context: { groupAddress: config.groupAddress },
      });
    }

    if (
      !Number.isInteger(config.groupAddress) ||
      config.groupAddress < 0 ||
      config.groupAddress > 255
    ) {
      throw new ValidationError(
        'groupAddress must be an integer between 0-255',
        {
          context: { groupAddress: config.groupAddress },
        }
      );
    }
  }

  /**
   * Generate error context for consistent error reporting.
   * @param operation - Operation being performed
   * @param additional - Additional context data
   * @returns Error context object
   */
  protected getErrorContext(
    operation: string,
    additional: Record<string, unknown> = {}
  ): Record<string, unknown> {
    return {
      transportType: this.constructor.name,
      state: this.connectionState,
      operation,
      localAddress: this.config.localAddress,
      groupAddress: this.config.groupAddress,
      timestamp: new Date().toISOString(),
      connectedSince: this.connectStartTime?.toISOString(),
      ...additional,
    };
  }

  /**
   * Emit a transport error event.
   * @param error - Error to emit
   */
  protected emitError(error: Error): void {
    this.emit('error', error);
  }

  /**
   * Handle unexpected disconnection.
   * Should be called by transport implementations when connection is lost.
   */
  protected handleUnexpectedDisconnect(reason?: Error): void {
    const wasConnected = this.connectionState === 'connected';
    this.connectionState = 'disconnected';
    this.connectStartTime = undefined;

    if (wasConnected) {
      this.emit('disconnect');

      if (reason) {
        this.emitError(
          new DisconnectionError('Transport disconnected unexpectedly', {
            cause: reason,
            context: this.getErrorContext('unexpected_disconnect'),
          })
        );
      }
    }
  }
}
